import type { CompanyAvatarChange } from "@rakazo/contracts";
import { COMPANY_ICON_NAMES, PROFILE_IMAGE_MAX_BYTES } from "@rakazo/contracts";
import { Button } from "@rakazo/ui-web";
import {
  Building2,
  BriefcaseBusiness,
  Globe,
  Landmark,
  Heart,
  Code2,
  Megaphone,
  ShoppingBag,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { PhotoCropDialog } from "../../components/PhotoCropDialog";
import { errorText } from "../../lib/user-error";

const icons = {
  building: Building2,
  briefcase: BriefcaseBusiness,
  globe: Globe,
  landmark: Landmark,
  heart: Heart,
  code: Code2,
  megaphone: Megaphone,
  shopping: ShoppingBag,
};
const labels = {
  building: "Building",
  briefcase: "Briefcase",
  globe: "Globe",
  landmark: "Landmark",
  heart: "Heart",
  code: "Code",
  megaphone: "Megaphone",
  shopping: "Shopping",
};
export function CompanyAvatar({
  value,
  className = "size-6",
}: {
  value?: string | null;
  className?: string;
}) {
  const [failed, setFailed] = useState<string | null>(null);
  if (value?.startsWith("data:image/") && failed !== value)
    return (
      <img
        src={value}
        alt=""
        className={`${className} rounded-lg object-contain`}
        onError={() => setFailed(value)}
      />
    );
  const name = value?.startsWith("icon:") ? value.slice(5) : "building";
  const Icon = icons[name as keyof typeof icons] ?? Building2;
  return <Icon className={className} aria-hidden="true" />;
}
export function CompanyAvatarEditor({
  value,
  disabled,
  onError,
  onSave,
}: {
  value: string | null;
  disabled: boolean;
  onError: (message: string | null) => void;
  onSave: (avatar: CompanyAvatarChange) => Promise<void>;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<CompanyAvatarChange | null>(null);
  const [reading, setReading] = useState(false);
  const [cropSource, setCropSource] = useState<string | null>(null);
  const request = useRef(0);
  useEffect(() => {
    setPending(null);
  }, [value]);
  useEffect(
    () => () => {
      request.current++;
    },
    [],
  );
  const busy = disabled || reading;
  const preview =
    pending?.kind === "icon" ? `icon:${pending.icon}` : pending?.kind === "none" ? null : value;

  async function upload(file: File) {
    if (
      !file.size ||
      file.size > PROFILE_IMAGE_MAX_BYTES ||
      !["image/png", "image/jpeg", "image/webp", "image/gif"].includes(file.type)
    ) {
      onError("Choose a PNG, JPEG, WebP or GIF image up to 5 MB.");
      return;
    }
    const current = ++request.current;
    setReading(true);
    onError(null);
    try {
      const source = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () =>
          typeof reader.result === "string"
            ? resolve(reader.result)
            : reject(new Error("Could not read image"));
        reader.onerror = () => reject(new Error("Could not read image"));
        reader.readAsDataURL(file);
      });
      if (current === request.current) setCropSource(source);
    } catch {
      if (current === request.current) onError("Could not read this image. Choose another file.");
    } finally {
      if (current === request.current) setReading(false);
    }
  }

  async function saveSelection() {
    if (!pending || busy) return;
    onError(null);
    try {
      await onSave(pending);
      setPending(null);
    } catch (cause) {
      onError(errorText(cause, "Could not save avatar"));
    }
  }

  return (
    <div className="space-y-3 border-b border-border pb-5" aria-label="Company avatar">
      <PhotoCropDialog
        source={cropSource}
        fit="contain"
        shape="square"
        onCancel={() => setCropSource(null)}
        onApply={async (cropped) => {
          onError(null);
          await onSave({ kind: "image", base64: cropped.slice(cropped.indexOf(",") + 1) });
          setPending(null);
        }}
      />
      <h3 className="font-medium">Avatar</h3>
      <div className="flex flex-wrap items-center gap-4">
        <div
          className={`flex shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-muted ${preview?.startsWith("data:image/") ? "size-28" : "size-16 p-2"}`}
          aria-label="Company avatar preview"
        >
          <CompanyAvatar value={preview} className="size-full" />
        </div>
        <Button variant="outline" disabled={busy} onClick={() => input.current?.click()}>
          Upload image
        </Button>
        <input
          ref={input}
          type="file"
          className="sr-only"
          aria-label="Upload Company avatar"
          accept="image/png,image/jpeg,image/webp,image/gif"
          disabled={busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) void upload(file);
          }}
        />
        {preview?.startsWith("data:image/") ? (
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => {
              onError(null);
              setCropSource(preview);
            }}
          >
            Adjust Photo
          </Button>
        ) : null}
        <Button
          variant="ghost"
          disabled={busy || (!value && !pending)}
          onClick={() => {
            request.current++;
            setPending({ kind: "none" });
            onError(null);
          }}
        >
          Reset to default
        </Button>
      </div>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Company icons">
        {COMPANY_ICON_NAMES.map((name) => (
          <button
            type="button"
            key={name}
            aria-label={`Use ${labels[name]} icon`}
            aria-pressed={preview === `icon:${name}` || (!preview && name === "building")}
            disabled={busy}
            onClick={() => {
              setPending({ kind: "icon", icon: name });
              onError(null);
            }}
            className={`flex size-11 items-center justify-center rounded-lg border border-border hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 ${preview === `icon:${name}` || (!preview && name === "building") ? "bg-accent" : ""}`}
          >
            <CompanyAvatar value={`icon:${name}`} className="size-5" />
          </button>
        ))}
      </div>
      <Button disabled={busy || !pending} onClick={() => void saveSelection()}>
        {disabled ? "Saving…" : "Save avatar"}
      </Button>
    </div>
  );
}
